import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { nullOrUndefinedResolver } from '../../../utils'
import { PromoteDemoteState, SetContentItemToPromoteDemote } from '../types'
import { findContentItemDepth } from '../utils/depthCalculator'
import { validateEligibility } from '../utils/validators'

/** How long the just-moved row stays highlighted, in ms. */
const LOCAL_MOVE_HIGHLIGHT_DURATION_MS = 2000

/** Default state for selection-related fields only — isPromoteDemoteInFlight and
 * recentlyMovedNodeId are controlled solely by their own setters. */
const defaultPromoteDemoteState = {
    contentItemToPromoteDemote: null,
    isInProject: false,
    isUserAdded: false,
    isL1Paragraph: false,
    isArticle: false,
    paragraphLevel: 0,
    depth: 0,
}

export const usePromoteDemoteStore = create<PromoteDemoteState>((set, get) => ({
    ...defaultPromoteDemoteState,
    isPromoteDemoteInFlight: false,
    recentlyMovedNodeId: null,
    canBePromoted: (isContentTeamFromStudioPage: boolean = false) => {
        const state = get()
        const options = {
            contentItemToPromoteDemote: state.contentItemToPromoteDemote,
            isInProject: state.isInProject,
            isUserAdded: state.isUserAdded,
            isL1Paragraph: state.isL1Paragraph,
            isArticle: state.isArticle,
            paragraphLevel: state.paragraphLevel,
            depth: state.depth,
            isContentTeamFromStudioPage,
        }
        const { canBePromoted } = validateEligibility(options)

        return canBePromoted
    },

    canBeDemoted: (isContentTeamFromStudioPage: boolean = false) => {
        const state = get()
        const options = {
            contentItemToPromoteDemote: state.contentItemToPromoteDemote,
            isInProject: state.isInProject,
            isUserAdded: state.isUserAdded,
            isL1Paragraph: state.isL1Paragraph,
            isArticle: state.isArticle,
            paragraphLevel: state.paragraphLevel,
            depth: state.depth,
            isContentTeamFromStudioPage,
        }
        const { canBeDemoted } = validateEligibility(options)

        return canBeDemoted
    },

    setPromoteDemoteInFlight: (value: boolean) => set({ isPromoteDemoteInFlight: value }),

    // Self-clearing: a second move within the window isn't cut short by the
    // first move's timer, since the timer only clears if it's still current.
    setRecentlyMovedNode: (id: string) => {
        set({ recentlyMovedNodeId: id })
        setTimeout(() => {
            if (get().recentlyMovedNodeId === id) set({ recentlyMovedNodeId: null })
        }, LOCAL_MOVE_HIGHLIGHT_DURATION_MS)
    },

    resetPromoteDemoteState: () => set(defaultPromoteDemoteState),

    // Action to update the state based on content analysis
    setContentItemToPromoteDemote: (
        props: Partial<SetContentItemToPromoteDemote> | null
    ) => {
        if (!props || !props.contentItemViewNodes?.length) {
            // reset state if no content item or nodes are provided
            set(defaultPromoteDemoteState)
            return
        }

        const {
            contentItemViewNodes,
            contentItemToPromoteDemote,
            isInProject,
        } = nullOrUndefinedResolver(props)

        const treeData = useWorkspace2Store.getState().treeData
        // calculate depth ONCE here
        const depth = contentItemToPromoteDemote.id
            ? findContentItemDepth(contentItemToPromoteDemote.id, treeData)
            : 0

        // find the content item
        const content = contentItemViewNodes?.find((node) =>
            node.elements?.some(
                (element) => element.id === contentItemToPromoteDemote.id
            )
        )

        // extract content required properties for evaluation
        const paragraphLevel = content?.order || 0
        const isArticle = content?.textType === 'Article'
        const isUserAdded = content?.isUserAdded || false
        // determine if content is an L1 paragraph
        const isL1Paragraph = Boolean(
            content &&
                contentItemViewNodes?.some((node) => {
                    if (content.dtvContentItemParentId) {
                        return (
                            node.id === content.dtvContentItemParentId &&
                            node.textType === 'Article'
                        )
                    }
                    return (
                        node.dtvItemId === content.dtvParentId &&
                        node.textType === 'Article'
                    )
                })
        )

        const resolvedDepth = depth >= 0 ? depth : 0

        // Skip the write when nothing changed — every mounted row's registration
        // effect calls this on each tree change.
        const current = get()
        const isUnchanged =
            current.contentItemToPromoteDemote?.id === contentItemToPromoteDemote.id &&
            current.isInProject === isInProject &&
            current.isUserAdded === isUserAdded &&
            current.isL1Paragraph === isL1Paragraph &&
            current.isArticle === isArticle &&
            current.paragraphLevel === paragraphLevel &&
            current.depth === resolvedDepth
        if (isUnchanged) return

        set({
            contentItemToPromoteDemote,
            isInProject,
            isUserAdded,
            isL1Paragraph,
            isArticle,
            paragraphLevel,
            depth: resolvedDepth,
        })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `usePromoteDemoteStore` store.
 * Sample usage:
 * ```typescript
 * const { contentItemToPromoteDemote, setContentItemToPromoteDemote } = usePromoteDemoteSelector((state) => ({
 *   contentItemToPromoteDemote: state.contentItemToPromoteDemote,
 *   setContentItemToPromoteDemote: state.setContentItemToPromoteDemote,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const usePromoteDemoteSelector = <T>(
    selector: (state: PromoteDemoteState) => T
): T => usePromoteDemoteStore(useShallow(selector))
